import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { FadeIn } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, xcss } from '@atlaskit/primitives';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { AISources, ErrorBoundary as CommonErrorBoundary, useAnalytics } from '../../common';
import { SainAction, useSAINUsageCriteriaMet } from '../../common/controller/analytics';
import { OpenChatButton } from '../../common/ui';
import { DotAnimationLoader } from '../../common/ui/ai-answer-loading';
import { AiBrand } from '../../constants';
import { AIAnswerBody } from '../ai-answer-body';
import { useAIAnswerContext } from '../ai-answer-context';
import { AIAnswerFooter } from '../ai-answer-footer';
import { AIAnswerTitle } from '../ai-answer-title';
import { onAIAnswerLoadingDialogShown, onAIAnswerSourceClick } from '../analytics';
import { ReadingAidsFollowUps } from '../reading-aids-follow-ups';
import { ReadingAidsContainer, ReadingAidsPopupContainer, ReadingAidsWrapper } from '../styled';
import { EditDefinition } from './edit-definition';
import { messages } from './messages';
var sourcesContainerStyles = xcss({
  paddingTop: '0',
  paddingRight: 'space.300',
  paddingBottom: 'space.100',
  paddingLeft: 'space.300'
});
var openChatContainerStyles = xcss({
  paddingBlockEnd: 'space.100',
  paddingInline: 'space.300'
});
var MAX_SOURCES = 4;
export var DefinitionAnswerState = /*#__PURE__*/function (DefinitionAnswerState) {
  DefinitionAnswerState["LOADING"] = "loading";
  DefinitionAnswerState["ANSWERED"] = "answered";
  DefinitionAnswerState["ERRORED"] = "errored";
  return DefinitionAnswerState;
}({});
var noop = function noop() {};
var OpenChat = function OpenChat(_ref) {
  var answerString = _ref.answerString,
    query = _ref.query;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  if (!fg('kd_definitions_open_chat')) {
    return /*#__PURE__*/React.createElement(React.Fragment, null);
  }
  return /*#__PURE__*/React.createElement(Box, {
    xcss: openChatContainerStyles
  }, /*#__PURE__*/React.createElement(OpenChatButton, {
    prompt: formatMessage(messages.definition_open_chat_user_initial_query, {
      // query only contains the highlighted word
      // we want to prefix the word "define" before
      // when a user's context is moved to chat
      term: query
    }),
    query: query,
    answerString: answerString,
    source: 'reading-aids'
  }));
};
var ReadingAidsPopupContent = function ReadingAidsPopupContent() {
  var _useAIAnswerContext = useAIAnswerContext(),
    brand = _useAIAnswerContext.brand,
    commonAttributes = _useAIAnswerContext.commonAttributes,
    callbacks = _useAIAnswerContext.callbacks,
    fireAnalyticsEvent = _useAIAnswerContext.fireAnalyticsEvent,
    readingAidsOptions = _useAIAnswerContext.readingAidsOptions,
    isAnswered = _useAIAnswerContext.isAnswered;
  var _useAIEventsInstrumen = useAIEventsInstrumentation(),
    trackAIResultAction = _useAIEventsInstrumen.trackAIResultAction;
  var _useSAINUsageCriteria = useSAINUsageCriteriaMet(),
    fireSAINUsageEvent = _useSAINUsageCriteria.fireSAINUsageEvent;
  var enableSmartLinkSources = readingAidsOptions === null || readingAidsOptions === void 0 ? void 0 : readingAidsOptions.enableSmartLinkSources;
  var searchSessionId = commonAttributes.searchSessionId,
    sources = commonAttributes.sources,
    answerString = commonAttributes.answerString,
    query = commonAttributes.query;
  var _ref2 = readingAidsOptions || {},
    definitionCurationOptions = _ref2.definitionCurationOptions;
  var editDefinitionEnabled = !!definitionCurationOptions;
  var sourceClickCallback = useCallback(function (index, hostname) {
    var _callbacks$onAIAnswer;
    fireAnalyticsEvent(onAIAnswerSourceClick(_objectSpread({
      sourceIndex: index,
      hostname: hostname
    }, commonAttributes)));
    fg('kd_enable_new_usage_tracking') ? fireSAINUsageEvent({
      metCriteria: SainAction.SOURCE_CLICKED,
      commonAttributes: commonAttributes
    }) : trackAIResultAction(SainAction.SOURCE_CLICKED);
    callbacks === null || callbacks === void 0 || (_callbacks$onAIAnswer = callbacks.onAIAnswerSourceClick) === null || _callbacks$onAIAnswer === void 0 || _callbacks$onAIAnswer.call(callbacks);
  }, [fireAnalyticsEvent, commonAttributes, fireSAINUsageEvent, callbacks, trackAIResultAction]);
  return /*#__PURE__*/React.createElement(FadeIn, null, function (props) {
    return /*#__PURE__*/React.createElement(ReadingAidsWrapper, _extends({
      onClick: function onClick(e) {
        return e.stopPropagation();
      }
    }, props), /*#__PURE__*/React.createElement(AIAnswerTitle, null), /*#__PURE__*/React.createElement(AIAnswerBody, null), editDefinitionEnabled && /*#__PURE__*/React.createElement(EditDefinition, null), /*#__PURE__*/React.createElement(Box, {
      xcss: sourcesContainerStyles
    }, /*#__PURE__*/React.createElement(AISources, {
      sources: sources,
      searchSessionId: searchSessionId,
      onClickHandler: sourceClickCallback,
      limit: MAX_SOURCES,
      verticalLayout: true,
      enableSmartLinkSources: enableSmartLinkSources
    })), brand === AiBrand.ROVO && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CommonErrorBoundary, null, /*#__PURE__*/React.createElement(ReadingAidsFollowUps, null)), isAnswered && /*#__PURE__*/React.createElement(OpenChat, {
      answerString: answerString,
      query: query
    })), /*#__PURE__*/React.createElement(AIAnswerFooter, null));
  });
};
export var DefinitionAnswerCard = function DefinitionAnswerCard() {
  var previousAnswerState = useRef();
  var _useAIAnswerContext2 = useAIAnswerContext(),
    loading = _useAIAnswerContext2.loading,
    commonAttributes = _useAIAnswerContext2.commonAttributes,
    partialResponse = _useAIAnswerContext2.partialResponse,
    isAnswered = _useAIAnswerContext2.isAnswered,
    hasError = _useAIAnswerContext2.hasError,
    readingAidsOptions = _useAIAnswerContext2.readingAidsOptions;
  var _useAnalytics = useAnalytics(),
    _useAnalytics2 = _slicedToArray(_useAnalytics, 1),
    fireAnalyticsEvent = _useAnalytics2[0];
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    continueLoadingAnimation = _useState2[0],
    setContinueLoadingAnimation = _useState2[1];
  var source = commonAttributes.source;
  var isStreaming = !!partialResponse;
  var _ref3 = readingAidsOptions || {},
    _ref3$onAnswerStateCh = _ref3.onAnswerStateChange,
    onAnswerStateChange = _ref3$onAnswerStateCh === void 0 ? noop : _ref3$onAnswerStateCh;
  useLayoutEffect(function () {
    if (!loading) {
      var countdownRemainingAnimation = setTimeout(function () {
        setContinueLoadingAnimation(false);
      }, 3000);
      return function () {
        return clearTimeout(countdownRemainingAnimation);
      };
    }
  }, [loading]);
  useEffect(function () {
    // update answer state for external consumers
    // using a previous answer state to prevent multiple duplicate calls,
    // especially in the case of loading && streaming
    if (fg('kd_definitions_answer_state_signal')) {
      if (hasError && previousAnswerState.current !== DefinitionAnswerState.ERRORED) {
        onAnswerStateChange(DefinitionAnswerState.ERRORED);
        previousAnswerState.current = DefinitionAnswerState.ERRORED;
      } else if (isAnswered && previousAnswerState.current !== DefinitionAnswerState.ANSWERED) {
        onAnswerStateChange(DefinitionAnswerState.ANSWERED);
        previousAnswerState.current = DefinitionAnswerState.ANSWERED;
      } else if ((loading || isStreaming) && previousAnswerState.current !== DefinitionAnswerState.LOADING) {
        onAnswerStateChange(DefinitionAnswerState.LOADING);
        previousAnswerState.current = DefinitionAnswerState.LOADING;
      }
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [loading, isStreaming, isAnswered, hasError]);
  var onLoadingAnimationShown = useCallback(function () {
    fireAnalyticsEvent(onAIAnswerLoadingDialogShown(commonAttributes, true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fireAnalyticsEvent]);
  return loading || isStreaming ? /*#__PURE__*/React.createElement(DotAnimationLoader, {
    onAnimationShown: onLoadingAnimationShown,
    source: source,
    "data-testid": "ai-answer-animated-loader"
  }) : /*#__PURE__*/React.createElement(ReadingAidsPopupContainer, {
    continueAnimation: continueLoadingAnimation
  }, /*#__PURE__*/React.createElement(ReadingAidsContainer, null, /*#__PURE__*/React.createElement(ReadingAidsPopupContent, null)));
};